import { Box } from '@unity/cloud-ui';
import React, { useCallback, useEffect } from 'react';
import type { Suggestion } from '@coveo/headless';

export function SearchSuggestions(props: {
  suggestions: Suggestion[];
  onSuggestionsClick: (suggestion: Suggestion) => void;
  smallerVariant?: boolean;
  highlightedIndex: number;
  setHighlightedIndex: React.Dispatch<React.SetStateAction<number>>;
}) {
  const { highlightedIndex, setHighlightedIndex, smallerVariant, suggestions, onSuggestionsClick } = props;

  const handleKeyDownWindow = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (highlightedIndex < 0 || highlightedIndex === suggestions.length - 1) {
          setHighlightedIndex(0);
        } else {
          setHighlightedIndex(highlightedIndex + 1);
        }
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (highlightedIndex <= 0) {
          setHighlightedIndex(suggestions.length - 1);
        } else {
          setHighlightedIndex(highlightedIndex - 1);
        }
      }
      if (event.key === 'Enter') {
        setHighlightedIndex(-1);
        onSuggestionsClick(suggestions[highlightedIndex]);
      }
    },
    [highlightedIndex, onSuggestionsClick, setHighlightedIndex, suggestions],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDownWindow);
    return () => {
      window.removeEventListener('keydown', handleKeyDownWindow);
    };
  }, [handleKeyDownWindow, highlightedIndex]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [suggestions, setHighlightedIndex]);

  return (
    <Box
      sx={{
        position: 'absolute',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {suggestions.map((suggestion, index) => (
        <Box
          key={suggestion.rawValue}
          sx={{
            textAlign: 'left',
            padding: `${smallerVariant ? '.2rem' : '.5rem'} .5rem`,
            backgroundColor: ({ vars }) =>
              highlightedIndex === index
                ? vars.palette.background.paperElevation3
                : vars.palette.background.paperElevation1,
            cursor: 'pointer',
            borderLeft: ({ vars }) => `1px solid ${vars.palette.background.paperElevation1}`,
            borderRight: ({ vars }) => `1px solid ${vars.palette.background.paperElevation1}`,
            '&:hover': {
              backgroundColor: ({ vars }) => vars.palette.background.paperElevation3,
            },
            '&:last-child': {
              borderRadius: '0 0 .5rem .5rem',
              borderBottom: ({ palette }) => `1px solid ${palette.background.paperElevation1}`,
            },
          }}
          onClick={() => onSuggestionsClick(suggestion)}
        >
          {suggestion.rawValue}
        </Box>
      ))}
    </Box>
  );
}
